import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkle, Lock, FolderSimplePlus, CircleNotch, Hourglass, Check } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Slider } from './ui/slider';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LangContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

const hasActiveSub = (u) => {
  if (!u) return false;
  if (u.role === 'admin' || u.role === 'teacher') return true;
  if (!u.is_premium) return false;
  return !u.premium_until || new Date(u.premium_until) > new Date();
};

const detailMsg = (err, fallback) => {
  const d = err.response?.data?.detail;
  if (typeof d === 'string') return d;
  return d?.message || fallback;
};

export const AIFlashcardGeneratorButton = ({ folders, onSaved }) => {
  const { user } = useAuth();
  const { lang } = useLang();
  const isDe = lang === 'de';
  const [open, setOpen] = useState(false);
  const unlocked = hasActiveSub(user);

  return (
    <>
      <button
        onClick={() => (unlocked ? setOpen(true) : toast.info(isDe ? 'Der KI-Kartengenerator ist nur mit aktivem Abo verfügbar.' : 'El generador IA solo está disponible con suscripción activa.'))}
        className={`group w-full flex items-center gap-4 rounded-2xl p-4 sm:p-5 text-left transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-lg ${unlocked ? 'bg-[#1F3A44] text-white' : 'bg-stone-100 text-stone-500 border border-dashed border-stone-300'}`}
        data-testid="ai-flashcards-open-btn"
      >
        <span className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${unlocked ? 'bg-[#EAB308] text-[#1F3A44]' : 'bg-stone-200'}`}>
          {unlocked ? <Sparkle size={26} weight="fill" className="group-hover:rotate-12 transition-transform" /> : <Lock size={24} weight="bold" />}
        </span>
        <span className="flex-1 min-w-0">
          <span className="block font-heading font-bold text-base sm:text-lg">
            {isDe ? 'KI-Kartengenerator' : 'Generador de tarjetas con IA'}
          </span>
          <span className={`block text-xs sm:text-sm ${unlocked ? 'text-white/70' : ''}`}>
            {unlocked
              ? (isDe ? 'Thema + Niveau wählen — bis zu 50 Karten in Sekunden.' : 'Elige tema y nivel — hasta 50 tarjetas en segundos.')
              : (isDe ? 'Nur mit aktivem Abo verfügbar' : 'Solo con suscripción activa')}
          </span>
        </span>
      </button>
      {unlocked && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="ai-flashcards-dialog">
            <GeneratorBody isDe={isDe} user={user} folders={folders} onSaved={onSaved} />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

const GeneratorBody = ({ isDe, user, folders, onSaved }) => {
  const [topic, setTopic] = useState('');
  const [level, setLevel] = useState(LEVELS.includes(user?.spanish_level) ? user.spanish_level : 'A2');
  const [count, setCount] = useState(20);
  const [direction, setDirection] = useState('es->de');
  const [loading, setLoading] = useState(false);
  const [balance, setBalance] = useState(null);
  const [result, setResult] = useState(null);

  const generate = async (n = count) => {
    if (!topic.trim()) { toast.error(isDe ? 'Bitte gib ein Thema ein' : 'Escribe un tema'); return; }
    setLoading(true); setBalance(null); setResult(null);
    try {
      const res = await axios.post(`${API}/flashcards/ai-generate`, { topic: topic.trim(), level, count: n, direction }, { withCredentials: true, timeout: 120000 });
      setResult(res.data);
    } catch (err) {
      if (err.response?.status === 429 && typeof err.response.data?.detail === 'object') setBalance(err.response.data.detail);
      else toast.error(detailMsg(err, isDe ? 'Fehler bei der Generierung' : 'Error al generar'));
    } finally { setLoading(false); }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle className="font-heading flex items-center gap-2">
          <Sparkle size={22} weight="fill" className="text-[#EAB308]" />
          {isDe ? 'KI-Kartengenerator' : 'Generador de tarjetas con IA'}
        </DialogTitle>
        <DialogDescription>
          {isDe ? 'Die Karten haben dasselbe Format wie deine eigenen. Du entscheidest, welche du speicherst.' : 'Las tarjetas tienen el mismo formato que las tuyas. Tú decides cuáles guardar.'}
        </DialogDescription>
      </DialogHeader>
      {!result && (
        <div className="space-y-5 pt-2">
          <div>
            <label className="text-sm font-semibold text-[#292524]">{isDe ? 'Thema' : 'Tema'}</label>
            <input value={topic} onChange={e => setTopic(e.target.value)} maxLength={120}
              placeholder={isDe ? 'z. B. Im Restaurant, Reisen, Gefühle…' : 'p. ej. En el restaurante, viajes, emociones…'}
              className="mt-1 w-full border border-stone-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#5B8C9A]"
              data-testid="ai-flashcards-topic-input" onKeyDown={e => e.key === 'Enter' && generate()} />
          </div>
          <div>
            <label className="text-sm font-semibold text-[#292524]">{isDe ? 'Niveau' : 'Nivel'}</label>
            <div className="mt-1 flex gap-1.5 flex-wrap">
              {LEVELS.map(l => (
                <button key={l} onClick={() => setLevel(l)} data-testid={`ai-flashcards-level-${l}`}
                  className={`px-3.5 py-1.5 rounded-full text-sm font-bold transition-colors ${level === l ? 'bg-[#AB1C24] text-white' : 'bg-stone-100 text-stone-700 hover:bg-stone-200'}`}>{l}</button>
              ))}
            </div>
          </div>
          <div>
            <div className="flex justify-between text-sm">
              <label className="font-semibold text-[#292524]">{isDe ? 'Anzahl Karten' : 'Número de tarjetas'}</label>
              <span className="font-bold text-[#AB1C24]" data-testid="ai-flashcards-count-value">{count}</span>
            </div>
            <Slider className="mt-3" min={5} max={50} step={1} value={[count]} onValueChange={v => setCount(v[0])} data-testid="ai-flashcards-count-slider" />
          </div>
          <div className="flex gap-2">
            {[['es->de', 'Español → Deutsch'], ['de->es', 'Deutsch → Español']].map(([v, label]) => (
              <button key={v} onClick={() => setDirection(v)} data-testid={`ai-flashcards-dir-${v === 'es->de' ? 'esde' : 'dees'}`}
                className={`flex-1 px-3 py-2 rounded-xl text-xs sm:text-sm font-semibold border transition-colors ${direction === v ? 'border-[#5B8C9A] bg-[#5B8C9A]/10 text-[#1F3A44]' : 'border-stone-200 text-stone-600 hover:bg-stone-50'}`}>{label}</button>
            ))}
          </div>
          {balance && <BalanceNotice isDe={isDe} balance={balance} onRetry={(n) => { setCount(n); generate(n); }} />}
          <button onClick={() => generate()} disabled={loading}
            className="w-full py-3 rounded-xl bg-[#AB1C24] hover:bg-[#8A151C] text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-70"
            data-testid="ai-flashcards-generate-btn">
            {loading ? <CircleNotch size={18} className="animate-spin" /> : <Sparkle size={18} weight="fill" />}
            {loading ? (isDe ? 'Karten werden erstellt…' : 'Creando tarjetas…') : (isDe ? `${count} Karten generieren` : `Generar ${count} tarjetas`)}
          </button>
        </div>
      )}
      {result && (
        <AICardPreview isDe={isDe} result={result} folders={folders} onSaved={onSaved} onRestart={() => setResult(null)} />
      )}
    </>
  );
};

const BalanceNotice = ({ isDe, balance, onRetry }) => (
  <div className="rounded-xl border border-amber-300 bg-amber-50 text-amber-900 p-3 text-sm flex gap-2" data-testid="ai-flashcards-balance-notice">
    <Hourglass size={20} weight="duotone" className="flex-shrink-0 mt-0.5" />
    <div className="space-y-2">
      <p>{balance.message}</p>
      {balance.max_cards > 0 && (
        <button onClick={() => onRetry(balance.max_cards)} className="text-xs font-bold underline" data-testid="ai-flashcards-retry-fewer-btn">
          {isDe ? `Mit ${balance.max_cards} Karten generieren` : `Generar ${balance.max_cards} tarjetas`}
        </button>
      )}
    </div>
  </div>
);

const AICardPreview = ({ isDe, result, folders, onSaved, onRestart }) => {
  const [cards, setCards] = useState(result.cards.map((c, i) => ({ ...c, _i: i, _sel: !c.exists, _saved: false })));
  const [folder, setFolder] = useState(result.topic);
  const [saving, setSaving] = useState(false);
  const selected = cards.filter(c => c._sel && !c._saved);

  const save = async (list, folderName) => {
    if (!list.length) return;
    setSaving(true);
    try {
      const payload = list.map(({ word, translation, example_sentence, direction }) => ({ word, translation, example_sentence, direction }));
      const res = await axios.post(`${API}/flashcards/bulk`, { cards: payload, folder: folderName || null }, { withCredentials: true });
      const ids = new Set(list.map(c => c._i));
      setCards(prev => prev.map(c => (ids.has(c._i) ? { ...c, _saved: true, _sel: false } : c)));
      toast.success(isDe ? `${res.data.saved} Karte(n) gespeichert` : `${res.data.saved} tarjeta(s) guardada(s)`);
      onSaved?.();
    } catch (err) { toast.error(detailMsg(err, 'Fehler')); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-3 pt-1" data-testid="ai-flashcards-preview">
      <div className="flex items-center justify-between text-xs text-[#57534E]">
        <span>{result.cards.length} {isDe ? 'Karten' : 'tarjetas'} · {result.level} · „{result.topic}"</span>
        <button onClick={() => setCards(p => p.map(c => (c._saved ? c : { ...c, _sel: selected.length === 0 })))} className="font-semibold text-[#5B8C9A] hover:underline" data-testid="ai-flashcards-toggle-all">
          {selected.length ? (isDe ? 'Keine auswählen' : 'Quitar selección') : (isDe ? 'Alle auswählen' : 'Seleccionar todas')}
        </button>
      </div>
      <div className="space-y-1.5 max-h-[42vh] overflow-y-auto pr-1">
        {cards.map(c => (
          <PreviewRow key={c._i} c={c} isDe={isDe} saving={saving}
            onToggle={() => setCards(p => p.map(x => (x._i === c._i ? { ...x, _sel: !x._sel } : x)))}
            onSaveOne={() => save([c], null)} />
        ))}
      </div>
      <div className="border-t border-stone-200 pt-3 space-y-2">
        <label className="text-sm font-semibold text-[#292524] flex items-center gap-1.5">
          <FolderSimplePlus size={18} className="text-[#5B8C9A]" /> {isDe ? 'Ordner' : 'Carpeta'}
        </label>
        <input list="ai-folder-options" value={folder} onChange={e => setFolder(e.target.value)} maxLength={60}
          className="w-full border border-stone-300 rounded-xl px-3 py-2 text-sm" data-testid="ai-flashcards-folder-input" />
        <datalist id="ai-folder-options">{(folders || []).map(f => <option key={f.name} value={f.name} />)}</datalist>
        <div className="flex flex-col sm:flex-row gap-2">
          <button onClick={() => save(selected, folder.trim())} disabled={saving || !selected.length || !folder.trim()}
            className="flex-1 py-2.5 rounded-xl bg-[#AB1C24] hover:bg-[#8A151C] text-white text-sm font-semibold disabled:opacity-50" data-testid="ai-flashcards-save-folder-btn">
            {isDe ? `${selected.length} im Ordner speichern` : `Guardar ${selected.length} en carpeta`}
          </button>
          <button onClick={onRestart} className="sm:w-40 py-2.5 rounded-xl bg-stone-100 hover:bg-stone-200 text-sm font-semibold" data-testid="ai-flashcards-restart-btn">
            {isDe ? 'Neues Thema' : 'Nuevo tema'}
          </button>
        </div>
      </div>
    </div>
  );
};

const PreviewRow = ({ c, isDe, saving, onToggle, onSaveOne }) => (
  <div className={`flex items-start gap-3 rounded-xl border px-3 py-2 ${c._saved ? 'border-emerald-200 bg-emerald-50' : c._sel ? 'border-[#5B8C9A]/50 bg-[#5B8C9A]/5' : 'border-stone-200'}`} data-testid={`ai-card-${c._i}`}>
    <input type="checkbox" checked={c._sel} disabled={c._saved} onChange={onToggle} className="mt-1 accent-[#AB1C24]" data-testid={`ai-card-check-${c._i}`} />
    <div className="flex-1 min-w-0">
      <p className="text-sm"><span className="font-bold text-[#292524]">{c.word}</span> <span className="text-[#57534E]">→ {c.translation}</span></p>
      {c.example_sentence && <p className="text-xs text-[#78716C] italic truncate">"{c.example_sentence}"</p>}
      {c.exists && !c._saved && <p className="text-[10px] font-semibold text-amber-700">{isDe ? 'Schon in deinem Deck' : 'Ya está en tu mazo'}</p>}
    </div>
    {c._saved ? (
      <span className="text-emerald-700 text-xs font-bold flex items-center gap-1"><Check size={14} weight="bold" />{isDe ? 'Gespeichert' : 'Guardada'}</span>
    ) : (
      <button onClick={onSaveOne} disabled={saving} className="text-xs px-2.5 py-1 rounded-full bg-stone-100 hover:bg-stone-200 font-semibold whitespace-nowrap" data-testid={`ai-card-save-${c._i}`}>
        {isDe ? 'Einzeln speichern' : 'Guardar suelta'}
      </button>
    )}
  </div>
);
